import { FC, useMemo } from "react";
import { useTaxonomyContext } from "../../../../contexts/Taxonomy/TaxonomyContext";
import { pluralize } from "../../../../utils/text";
import { ICPDistributionChart } from "./ICPDistributionChart";

interface IndustryDistributionItem {
  industryId: number;
  share: number;
  isSignificant: boolean;
  count: number;
}

interface ICPIndustryChartProps {
  /** Industry distribution data from API */
  data: IndustryDistributionItem[];
  /** Height of the chart container */
  height?: string;
  /** Whether the chart is loading */
  isLoading?: boolean;
}

/**
 * Component that displays industry distribution chart with proper industry name mapping
 */
export const ICPIndustryChart: FC<ICPIndustryChartProps> = ({
  data,
  height,
  isLoading = false,
}) => {
  const { hgIndustries } = useTaxonomyContext();

  const industriesMap = useMemo(() => {
    const map = {} as Record<number, string>;
    for (const industry of hgIndustries || []) {
      if (typeof industry.id === "number") {
        map[industry.id] = industry.name || "";
      }
    }
    return map;
  }, [hgIndustries]);

  const chartData = useMemo(() => {
    return [...data]
      .sort((a, b) => b.share - a.share)
      .slice(0, 10)
      .map((item) => ({
        category:
          industriesMap[item.industryId] || `Industry ${item.industryId}`,
        value: item.share,
        count: item.count,
        countText: pluralize("company", item.count),
      }));
  }, [data, industriesMap]);

  const totalCount = useMemo(() => {
    return data.reduce((sum, item) => sum + item.count, 0);
  }, [data]);

  return (
    <ICPDistributionChart
      title="Industry Distribution"
      data={chartData}
      height={height}
      totalCount={totalCount}
      columnLabelFormat="{valueY}% ({count})"
      columnTooltipFormat="{categoryX}: [bold]{valueY}%[/] ({count} {countText})"
      isLoading={isLoading}
    />
  );
};
